import React, { useState } from "react";
import { Loader2, Star, CheckCircle2, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import { api } from "@/lib/api";

export default function ConfirmFees() {
  const [f, setF] = useState({ receipt: "", amount: "", mobile: "", website: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.receipt.trim()) { toast.error("Enter the receipt number"); return; }
    if (!(parseInt(f.amount, 10) > 0)) { toast.error("Enter the amount paid"); return; }
    if (!/^[6-9]\d{9}$/.test(f.mobile.trim())) { toast.error("Enter a valid 10-digit mobile number"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/public/fee-confirm", { receipt_number: f.receipt.trim(), amount: parseInt(f.amount, 10), mobile: f.mobile.trim(), website: f.website });
      setDone(data);
      window.location.href = data.whatsapp_url;
    } catch (err) { toast.error(err.response?.data?.detail || "Could not submit. Please try again."); } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 via-white to-sky-50 px-4 py-10" data-testid="confirm-fees-page">
      <Seo title="Confirm Your Fees – Earn 5 Bonus Stars" />
      <div className="mx-auto max-w-md">
        <div className="text-center mb-6">
          <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-amber-400 shadow-lg shadow-amber-300/50"><Star className="h-8 w-8 text-white fill-white" /></div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900">Confirm Your Fees – Earn 5 Bonus Stars ⭐</h1>
          <p className="text-sm text-slate-500 mt-2">Prasad Tutor Group · Enter the details from your paper receipt.</p>
        </div>
        {done ? (
          <div className="rounded-2xl bg-white border border-green-200 p-6 text-center space-y-3 shadow-sm" data-testid="confirm-fees-done">
            <CheckCircle2 className="h-12 w-12 text-green-600 mx-auto" />
            <p className="font-bold text-slate-900">Thank you! Details saved on {done.submitted_at} IST.</p>
            <p className="text-sm text-slate-600">Please press <b>Send</b> in WhatsApp to complete your confirmation. Bonus stars are added after the office approves the receipt.</p>
            <a href={done.whatsapp_url} className="inline-flex items-center gap-2 rounded-full bg-green-600 px-5 py-2.5 text-sm font-bold text-white hover:bg-green-700" data-testid="confirm-fees-whatsapp"><MessageCircle className="h-4 w-4" /> Open WhatsApp</a>
            <button type="button" className="block mx-auto text-xs text-slate-500 underline" onClick={() => { setDone(null); setF({ receipt: "", amount: "", mobile: "", website: "" }); }} data-testid="confirm-fees-another">Confirm another receipt</button>
          </div>
        ) : (
          <form onSubmit={submit} className="rounded-2xl bg-white border border-slate-200 p-6 space-y-4 shadow-sm" data-testid="confirm-fees-form">
            <div><Label className="font-semibold">Receipt Number</Label><Input value={f.receipt} onChange={set("receipt")} maxLength={20} placeholder="e.g. 1322" className="h-12 text-lg" data-testid="confirm-fees-receipt" /></div>
            <div><Label className="font-semibold">Amount Paid (₹)</Label><Input type="number" inputMode="numeric" value={f.amount} onChange={set("amount")} placeholder="e.g. 8000" className="h-12 text-lg" data-testid="confirm-fees-amount" /></div>
            <div><Label className="font-semibold">Parent Mobile Number</Label><Input type="tel" inputMode="numeric" maxLength={10} value={f.mobile} onChange={(e) => setF((x) => ({ ...x, mobile: e.target.value.replace(/\D/g, "") }))} placeholder="10-digit mobile" className="h-12 text-lg" data-testid="confirm-fees-mobile" /></div>
            <input type="text" value={f.website} onChange={set("website")} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
            <Button type="submit" disabled={busy} className="w-full h-12 text-base bg-green-600 hover:bg-green-700" data-testid="confirm-fees-submit">{busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Submit & open WhatsApp"}</Button>
            <p className="text-[11px] text-slate-400 text-center">No smartphone? SMS "receipt number amount" (e.g. 1322 8000) to 9833388201.</p>
          </form>
        )}
      </div>
    </div>
  );
}
